import { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy, where,
  doc, setDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove,
} from "firebase/firestore";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import { DAY_NAMES, formatDayTime, todayKey } from "./lib/weekUtils";
import { DEFAULT_SETTINGS, timeToMin, minToTime, formatMin, blockApplies, BLOCK_MINUTES } from "./lib/booking";

export default function AdminPage({ user }) {
  var [bookingDoc, setBookingDoc] = useState(null);
  var [cfgEdit, setCfgEdit] = useState({}); // unsaved changes to the booking settings
  var [courtBlocks, setCourtBlocks] = useState([]);
  var [accessDoc, setAccessDoc] = useState(null);
  var [accessEdit, setAccessEdit] = useState({}); // unsaved padlock code changes
  var [accessSaved, setAccessSaved] = useState(false);
  var [upcomingBookings, setUpcomingBookings] = useState([]);
  var [blockForm, setBlockForm] = useState({ label: "Club session", dayOfWeek: "2", from: "19:00", to: "21:00", allCourts: true, courts: [], fromDate: "", toDate: "" });
  var [blockSkipInput, setBlockSkipInput] = useState({}); // blockId -> "YYYY-MM-DD"
  var [venues, setVenues] = useState([]);
  var [templates, setTemplates] = useState([]);
  var [messages, setMessages] = useState([]);
  var [players, setPlayers] = useState([]);
  var [dependents, setDependents] = useState([]);
  var [matches, setMatches] = useState([]);
  var [pollVotes, setPollVotes] = useState([]);
  var [fixNameInput, setFixNameInput] = useState({}); // uid -> typed name
  var [mergeTarget, setMergeTarget] = useState({}); // uid -> target uid

  var [venueName, setVenueName] = useState("");
  var [venueAddress, setVenueAddress] = useState("");
  var [venueCourts, setVenueCourts] = useState(2);
  var [venueIndoor, setVenueIndoor] = useState(false);
  var [editingVenueId, setEditingVenueId] = useState(null);
  var [venueEdit, setVenueEdit] = useState({});

  var [tplVenueId, setTplVenueId] = useState("");
  var [tplDay, setTplDay] = useState("2"); // Tuesday
  var [tplTime, setTplTime] = useState("19:00");
  var [tplEndTime, setTplEndTime] = useState("20:00");
  var [tplNote, setTplNote] = useState("For all standards — beginners welcome");
  var [editingTplId, setEditingTplId] = useState(null);
  var [tplEdit, setTplEdit] = useState({});
  var [excludeDateInput, setExcludeDateInput] = useState({}); // templateId -> "YYYY-MM-DD"

  useEffect(function () {
    var unsubVenues = onSnapshot(query(collection(db, "venues"), orderBy("name")), function (snap) {
      setVenues(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubTpl = onSnapshot(collection(db, "sessionTemplates"), function (snap) {
      setTemplates(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubMsgs = onSnapshot(collection(db, "contactMessages"), function (snap) {
      setMessages(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubPlayers = onSnapshot(collection(db, "users"), function (snap) {
      setPlayers(snap.docs.map(function (d) { return { uid: d.id, ...d.data() }; }));
    });
    var unsubDeps = onSnapshot(collection(db, "dependents"), function (snap) {
      setDependents(snap.docs.map(function (d) { var data = d.data(); return { uid: data.linkedUid || d.id, displayName: data.displayName }; }));
    });
    var unsubMatches = onSnapshot(collection(db, "matches"), function (snap) {
      setMatches(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubVotes = onSnapshot(collection(db, "pollVotes"), function (snap) {
      setPollVotes(snap.docs.map(function (d) { return d.data(); }));
    });
    var unsubBooking = onSnapshot(doc(db, "settings", "booking"), function (snap) {
      setBookingDoc(snap.exists() ? snap.data() : null);
    });
    var unsubAccess = onSnapshot(doc(db, "private", "access"), function (snap) {
      setAccessDoc(snap.exists() ? snap.data() : {});
    }, function () { setAccessDoc({}); });
    var unsubBlocks = onSnapshot(collection(db, "courtBlocks"), function (snap) {
      setCourtBlocks(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubUpcoming = onSnapshot(query(collection(db, "bookings"), where("date", ">=", todayKey())), function (snap) {
      setUpcomingBookings(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    return function () { unsubVenues(); unsubTpl(); unsubMsgs(); unsubPlayers(); unsubDeps(); unsubMatches(); unsubVotes(); unsubBooking(); unsubAccess(); unsubBlocks(); unsubUpcoming(); };
  }, []);

  function addCourtBlock(e) {
    e.preventDefault();
    if (!bookingDoc || !bookingDoc.venueId) { window.alert("Save the Court Booking settings first (venue, hours, prices)."); return; }
    var startMin = timeToMin(blockForm.from);
    var endMin = timeToMin(blockForm.to);
    if (!blockForm.label.trim()) { window.alert("Give the session a name, e.g. Club night."); return; }
    if (endMin <= startMin) { window.alert("The end time needs to be after the start time."); return; }
    if (!blockForm.allCourts && blockForm.courts.length === 0) { window.alert("Pick at least one court, or choose all courts."); return; }
    var candidate = {
      active: true, dayOfWeek: parseInt(blockForm.dayOfWeek, 10), startMin: startMin, endMin: endMin,
      courts: blockForm.allCourts ? [] : blockForm.courts.slice().sort(function (a, b) { return a - b; }),
      fromDate: blockForm.fromDate, toDate: blockForm.toDate, excludedDates: [],
    };
    // Existing bookings under the new session aren't removed automatically.
    var clashes = upcomingBookings.filter(function (b) {
      for (var m = b.startMin; m < b.endMin; m += BLOCK_MINUTES) {
        if (blockApplies(candidate, b.date, b.court, m)) return true;
      }
      return false;
    });
    if (clashes.length > 0 && !window.confirm(
      clashes.length + " existing booking(s) overlap this session (" +
      clashes.slice(0, 4).map(function (b) { return b.displayName + " " + b.date; }).join(", ") +
      (clashes.length > 4 ? "…" : "") + "). They won't be cancelled automatically — you'd need to cancel them on the Courts tab. Add the session anyway?"
    )) return;
    addDoc(collection(db, "courtBlocks"), {
      ...candidate, venueId: bookingDoc.venueId, label: blockForm.label.trim(), createdAt: new Date().toISOString(),
    });
  }

  function toggleBlockCourt(c) {
    setBlockForm(function (prev) {
      var has = prev.courts.indexOf(c) !== -1;
      return { ...prev, courts: has ? prev.courts.filter(function (x) { return x !== c; }) : prev.courts.concat([c]) };
    });
  }

  function addBlockSkip(b) {
    var d = blockSkipInput[b.id];
    if (!d) return;
    updateDoc(doc(db, "courtBlocks", b.id), { excludedDates: arrayUnion(d) });
    setBlockSkipInput(function (prev) { return { ...prev, [b.id]: "" }; });
  }

  var cfg = { ...DEFAULT_SETTINGS, venueId: "", ...(bookingDoc || {}), ...cfgEdit };
  // Floodlights: shown/edited as a time; stored as minutes after midnight (1440 = no charge).
  var lightsTime = cfgEdit.floodlightsFromTime !== undefined
    ? cfgEdit.floodlightsFromTime
    : (cfg.floodlightsFromMin < 1440 ? minToTime(cfg.floodlightsFromMin) : "");
  function setCfg(key, value) { setCfgEdit(function (prev) { return { ...prev, [key]: value }; }); }

  function saveBookingSettings() {
    var venue = venues.find(function (v) { return v.id === cfg.venueId; });
    if (!venue) { window.alert("Choose which venue courts are booked at first."); return; }
    setDoc(doc(db, "settings", "booking"), {
      venueId: venue.id,
      venueName: venue.name,
      courtCount: venue.courtCount || 1,
      openTime: cfg.openTime,
      closeTime: cfg.closeTime,
      pricePence: Math.round(parseFloat(cfg.pricePence) || 0),
      gameJoinFeePence: Math.round(parseFloat(cfg.gameJoinFeePence) || 0),
      // Rounded down to the half hour so a part-lit block is charged rather than missed.
      floodlightsFromMin: lightsTime ? Math.floor(timeToMin(lightsTime) / BLOCK_MINUTES) * BLOCK_MINUTES : 1440,
      floodlightRatePence: Math.round(parseFloat(cfg.floodlightRatePence) || 0),
      maxMinutes: parseInt(cfg.maxMinutes, 10) || 120,
      minMinutesMember: parseInt(cfg.minMinutesMember, 10) || 30,
      minMinutesNonMember: parseInt(cfg.minMinutesNonMember, 10) || 60,
      advanceDays: parseInt(cfg.advanceDays, 10) || 14,
      holdMinutes: parseInt(cfg.holdMinutes, 10) || 10,
    }).then(function () { setCfgEdit({}); });
  }

  // Shown to people after they book/pay. These defaults match what the payment server uses.
  var access = { padlockCode: "1066", floodlightCode: "", contactText: "Gareth on 07985 466355", ...(accessDoc || {}), ...accessEdit };
  function setAccessField(key, value) { setAccessSaved(false); setAccessEdit(function (prev) { return { ...prev, [key]: value }; }); }

  function saveAccess() {
    if (!String(access.padlockCode).trim()) { window.alert("Enter the padlock code."); return; }
    setDoc(doc(db, "private", "access"), {
      padlockCode: String(access.padlockCode).trim(),
      floodlightCode: String(access.floodlightCode || "").trim(),
      contactText: String(access.contactText || "").trim() || "Gareth on 07985 466355",
    }).then(function () { setAccessEdit({}); setAccessSaved(true); })
      .catch(function () { window.alert("Couldn't save — publish the latest firestore.rules in Firebase first."); });
  }

  function toggleMember(p) {
    updateDoc(doc(db, "users", p.uid), { isMember: !p.isMember });
  }

  function markAllMembers() {
    var targets = players.filter(function (p) { return p.active !== false && !p.isMember; });
    if (targets.length === 0) { window.alert("Everyone active is already a member."); return; }
    if (window.confirm("Mark all " + targets.length + " active players as members? Do this once to start with, then untick anyone who isn't.")) {
      targets.forEach(function (p) { updateDoc(doc(db, "users", p.uid), { isMember: true }); });
    }
  }

  function toggleHandled(m) {
    updateDoc(doc(db, "contactMessages", m.id), { handled: !m.handled });
  }

  function restoreName(uid) {
    var name = (fixNameInput[uid] || "").trim();
    if (!name) return;
    // Recreates the household entry at the exact same uid it had before it was
    // removed, so every past match/vote that already references this uid
    // resolves to a name again — nothing about the historical data changes.
    setDoc(doc(db, "dependents", uid), {
      ownerId: user.uid,
      displayName: name,
      createdAt: new Date().toISOString(),
    });
    setFixNameInput(function (prev) { return { ...prev, [uid]: "" }; });
  }

  function mergeInto(oldUid) {
    var newUid = mergeTarget[oldUid];
    if (!newUid) return;
    var targetName = players.find(function (p) { return p.uid === newUid; });
    if (!window.confirm(
      "Merge this player's history into " + (targetName ? targetName.displayName : "the selected account") +
      "? Every past match they're in will be rewritten to point at that account instead. This can't be undone."
    )) return;
    matches.filter(function (m) { return (m.players || []).indexOf(oldUid) !== -1; }).forEach(function (m) {
      var newTeamA = (m.teamA || []).map(function (u) { return u === oldUid ? newUid : u; });
      var newTeamB = (m.teamB || []).map(function (u) { return u === oldUid ? newUid : u; });
      updateDoc(doc(db, "matches", m.id), { teamA: newTeamA, teamB: newTeamB, players: newTeamA.concat(newTeamB) });
    });
    setMergeTarget(function (prev) { return { ...prev, [oldUid]: "" }; });
  }

  function togglePlayerActive(p) {
    var willDeactivate = p.active !== false;
    var msg = willDeactivate
      ? "Deactivate " + p.displayName + "? They'll be signed out and won't be able to log back in, but their past results and stats stay intact. You can reactivate them any time."
      : "Reactivate " + p.displayName + "? They'll be able to log in again.";
    if (window.confirm(msg)) {
      updateDoc(doc(db, "users", p.uid), { active: !willDeactivate });
    }
  }

  function addVenue(e) {
    e.preventDefault();
    if (!venueName.trim()) return;
    addDoc(collection(db, "venues"), {
      name: venueName.trim(),
      address: venueAddress.trim(),
      courtCount: parseInt(venueCourts, 10) || 1,
      indoor: venueIndoor,
      createdAt: new Date().toISOString(),
    });
    setVenueName(""); setVenueAddress(""); setVenueCourts(2); setVenueIndoor(false);
  }

  function startEditVenue(v) {
    setEditingVenueId(v.id);
    setVenueEdit({ name: v.name, address: v.address || "", courtCount: v.courtCount || 1, indoor: !!v.indoor });
  }

  function saveVenue(v) {
    updateDoc(doc(db, "venues", v.id), {
      name: venueEdit.name.trim(),
      address: venueEdit.address.trim(),
      courtCount: parseInt(venueEdit.courtCount, 10) || 1,
      indoor: venueEdit.indoor,
    });
    setEditingVenueId(null);
  }

  function addTemplate(e) {
    e.preventDefault();
    if (!tplVenueId) return;
    var venue = venues.find(function (v) { return v.id === tplVenueId; });
    addDoc(collection(db, "sessionTemplates"), {
      venueId: tplVenueId,
      venueName: venue ? venue.name : "",
      dayOfWeek: parseInt(tplDay, 10),
      time: tplTime,
      endTime: tplEndTime,
      note: tplNote.trim(),
      active: true,
      excludedDates: [],
      createdAt: new Date().toISOString(),
    });
    setTplNote("For all standards — beginners welcome");
  }

  function startEditTemplate(t) {
    setEditingTplId(t.id);
    setTplEdit({ venueId: t.venueId, dayOfWeek: String(t.dayOfWeek), time: t.time, endTime: t.endTime || "", note: t.note || "" });
  }

  function saveTemplate(t) {
    var venue = venues.find(function (v) { return v.id === tplEdit.venueId; });
    updateDoc(doc(db, "sessionTemplates", t.id), {
      venueId: tplEdit.venueId,
      venueName: venue ? venue.name : t.venueName,
      dayOfWeek: parseInt(tplEdit.dayOfWeek, 10),
      time: tplEdit.time,
      endTime: tplEdit.endTime,
      note: tplEdit.note.trim(),
    });
    setEditingTplId(null);
  }

  function toggleActive(tpl) {
    updateDoc(doc(db, "sessionTemplates", tpl.id), { active: !tpl.active });
  }

  function removeTemplate(tpl) {
    if (window.confirm("Delete this recurring session? This won't remove past results.")) {
      deleteDoc(doc(db, "sessionTemplates", tpl.id));
    }
  }

  function addExclusion(tpl) {
    var d = excludeDateInput[tpl.id];
    if (!d) return;
    updateDoc(doc(db, "sessionTemplates", tpl.id), { excludedDates: arrayUnion(d) });
    setExcludeDateInput(function (prev) { return { ...prev, [tpl.id]: "" }; });
  }

  function removeExclusion(tpl, d) {
    updateDoc(doc(db, "sessionTemplates", tpl.id), { excludedDates: arrayRemove(d) });
  }

  var sortedMessages = messages.slice().sort(function (a, b) {
    var order = (a.handled ? 1 : 0) - (b.handled ? 1 : 0);
    if (order !== 0) return order;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  var unhandledCount = messages.filter(function (m) { return !m.handled; }).length;
  var today = todayKey();
  var sortedPlayers = players.slice().sort(function (a, b) {
    var order = ((a.active === false) ? 1 : 0) - ((b.active === false) ? 1 : 0);
    if (order !== 0) return order;
    return (a.displayName || "").localeCompare(b.displayName || "");
  });

  // Uids that show up in match history but don't resolve to any known name —
  // e.g. a household member was removed (or a linked account's household
  // entry deleted) after they'd already played some matches under that id.
  var knownUids = {};
  players.forEach(function (p) { knownUids[p.uid] = true; });
  dependents.forEach(function (d) { knownUids[d.uid] = true; });
  var orphanedUids = Array.from(new Set(
    matches.flatMap(function (m) { return m.players || []; }).filter(function (uid) { return !knownUids[uid]; })
  ));
  var knownNames = {};
  players.forEach(function (p) { knownNames[p.uid] = p.displayName; });
  dependents.forEach(function (d) { knownNames[d.uid] = d.displayName; });

  // Poll votes store the voter's name permanently at the time they voted, so
  // even after a household entry is deleted, their old votes usually still
  // remember what they were called.
  var lastKnownName = {};
  pollVotes.slice().sort(function (a, b) { return new Date(a.votedAt) - new Date(b.votedAt); })
    .forEach(function (v) { if (v.uid && v.displayName) lastKnownName[v.uid] = v.displayName; });

  // A few recent matches for one orphaned uid, with teammates/opponents named
  // where possible — that context is usually enough to recognise who it was.
  function orphanContext(uid) {
    return matches
      .filter(function (m) { return (m.players || []).indexOf(uid) !== -1; })
      .sort(function (a, b) { return new Date(b.timestamp) - new Date(a.timestamp); })
      .slice(0, 5)
      .map(function (m) {
        var others = (m.players || []).filter(function (p) { return p !== uid; })
          .map(function (p) { return knownNames[p] || "(also unknown)"; });
        var when = m.timestamp ? new Date(m.timestamp).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
        return when + " · " + (m.venueName || "") + " · with/vs " + others.join(", ");
      });
  }

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 16 }}>Admin</h2>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>
          {"Contact Messages"}{unhandledCount > 0 ? " (" + unhandledCount + " new)" : ""}
        </h3>
        {sortedMessages.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No messages yet.</p>}
        {sortedMessages.map(function (m) {
          return (
            <div key={m.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, opacity: m.handled ? 0.55 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: colors.textDim }}>{m.email + " · " + m.phone}</div>
                </div>
                <button onClick={function () { toggleHandled(m); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 11 }}>
                  {m.handled ? "Mark unread" : "Mark handled"}
                </button>
              </div>
              {m.message && <p style={{ fontSize: 13, color: colors.text, margin: "8px 0 0" }}>{m.message}</p>}
              <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 6 }}>{new Date(m.createdAt).toLocaleString("en-GB")}</div>
            </div>
          );
        })}
      </div>

      {orphanedUids.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 20, borderColor: colors.dangerBorder }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Fix Missing Names</h3>
          <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
            These players show up in match history but their name can't be found anymore — usually because a
            household entry for them was removed. Either give them their name back, or merge their history into
            their real account if they've since registered one.
          </p>
          {orphanedUids.map(function (uid) {
            var context = orphanContext(uid);
            var suggested = lastKnownName[uid];
            return (
              <div key={uid} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border }}>
                <div style={{ fontSize: 11, color: colors.textFaint, fontFamily: "monospace", marginBottom: 6 }}>{uid}</div>
                {suggested && (
                  <div style={{ fontSize: 12, color: colors.accent2, marginBottom: 4, fontWeight: 700 }}>
                    Found in old poll records: {suggested}
                  </div>
                )}
                <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 8 }}>
                  {context.length === 0 ? "No match history to go on." : context.map(function (line, i) {
                    return <div key={i}>{line}</div>;
                  })}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input
                    style={inputStyle}
                    placeholder="Restore their name…"
                    value={fixNameInput[uid] !== undefined ? fixNameInput[uid] : (suggested || "")}
                    onChange={function (e) { setFixNameInput(function (prev) { return { ...prev, [uid]: e.target.value }; }); }}
                  />
                  <button onClick={function () { restoreName(uid); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px", fontSize: 12 }}>Restore</button>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    style={inputStyle}
                    value={mergeTarget[uid] || ""}
                    onChange={function (e) { setMergeTarget(function (prev) { return { ...prev, [uid]: e.target.value }; }); }}
                  >
                    <option value="">…or merge into an existing account</option>
                    {sortedPlayers.map(function (p) { return <option key={p.uid} value={p.uid}>{p.displayName}</option>; })}
                  </select>
                  <button onClick={function () { mergeInto(uid); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px", fontSize: 12 }}>Merge</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Players</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Deactivating blocks login and hides them from session rosters, but keeps their name on past results
          intact — it's reversible. There's no permanent delete here on purpose (it would break everyone else's
          match history); if you really need to erase someone entirely, that has to be done directly in the
          Firebase console (Authentication + Firestore).
        </p>
        {sortedPlayers.map(function (p) {
          var isSelf = user && p.uid === user.uid;
          var deactivated = p.active === false;
          return (
            <div key={p.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 14, opacity: deactivated ? 0.55 : 1 }}>
              <div>
                <strong>{p.displayName}</strong>
                {p.isAdmin && <span style={{ fontSize: 11, color: colors.accent2, marginLeft: 6 }}>admin</span>}
                {p.isMember && <span style={{ fontSize: 11, color: colors.teal, marginLeft: 6 }}>member</span>}
                {deactivated && <span style={{ fontSize: 11, color: colors.danger, marginLeft: 6 }}>deactivated</span>}
                <div style={{ fontSize: 12, color: colors.textDim }}>{p.email + (p.phone ? " · " + p.phone : "")}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={function () { toggleMember(p); }}
                  style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: p.isMember ? colors.tealDark : colors.textDim }}
                >
                  {p.isMember ? "✓ Member" : "Make member"}
                </button>
                {!isSelf && (
                  <button
                    onClick={function () { togglePlayerActive(p); }}
                    style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: deactivated ? colors.accent2 : colors.danger, borderColor: deactivated ? colors.borderStrong : colors.dangerBorder }}
                  >
                    {deactivated ? "Reactivate" : "Deactivate"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <button onClick={markAllMembers} style={{ ...btnSecondaryStyle, marginTop: 12, fontSize: 13 }}>Mark all active players as members (one-off)</button>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Court Booking</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Members book free. Everyone else pays the price below per 30 minutes, for at least the minimum length.
          The number of courts comes from the venue's court count (see Venues).
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={labelStyle}>Venue courts are booked at</div>
            <select style={inputStyle} value={cfg.venueId} onChange={function (e) { setCfg("venueId", e.target.value); }}>
              <option value="">Select a venue…</option>
              {venues.map(function (v) { return <option key={v.id} value={v.id}>{v.name + " (" + (v.courtCount || 1) + " courts)"}</option>; })}
            </select>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Opens</div>
              <input style={inputStyle} type="time" value={cfg.openTime} onChange={function (e) { setCfg("openTime", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Closes</div>
              <input style={inputStyle} type="time" value={cfg.closeTime} onChange={function (e) { setCfg("closeTime", e.target.value); }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Non-member price per 30 min (pence)</div>
              <input style={inputStyle} type="number" min="0" value={cfg.pricePence} onChange={function (e) { setCfg("pricePence", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Longest booking (minutes)</div>
              <input style={inputStyle} type="number" min="30" step="30" value={cfg.maxMinutes} onChange={function (e) { setCfg("maxMinutes", e.target.value); }} />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Fee for a non-member to join someone's open game (pence)</div>
            <input style={inputStyle} type="number" min="0" value={cfg.gameJoinFeePence} onChange={function (e) { setCfg("gameJoinFeePence", e.target.value); }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Shortest — members (min)</div>
              <input style={inputStyle} type="number" min="30" step="30" value={cfg.minMinutesMember} onChange={function (e) { setCfg("minMinutesMember", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Shortest — non-members (min)</div>
              <input style={inputStyle} type="number" min="30" step="30" value={cfg.minMinutesNonMember} onChange={function (e) { setCfg("minMinutesNonMember", e.target.value); }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Book up to (days ahead)</div>
              <input style={inputStyle} type="number" min="1" value={cfg.advanceDays} onChange={function (e) { setCfg("advanceDays", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Payment hold (minutes)</div>
              <input style={inputStyle} type="number" min="5" value={cfg.holdMinutes} onChange={function (e) { setCfg("holdMinutes", e.target.value); }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Floodlights on from (blank = no charge)</div>
              <input style={inputStyle} type="time" step="1800" value={lightsTime} onChange={function (e) { setCfg("floodlightsFromTime", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Floodlight charge per hour (pence)</div>
              <input style={inputStyle} type="number" min="0" value={cfg.floodlightRatePence} onChange={function (e) { setCfg("floodlightRatePence", e.target.value); }} />
            </div>
          </div>
          <p style={{ fontSize: 11, color: colors.textFaint, margin: 0 }}>
            Floodlights are charged to members and non-members alike (not admins), per half hour from the lights-on time. Change the
            time as the seasons change — it applies to new bookings only, not ones already made. A member's booking that includes
            floodlit time has to be paid through Stripe like a non-member's.
          </p>
          <button style={btnStyle} onClick={saveBookingSettings}>Save Booking Settings</button>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Access Codes</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Shown on the thank-you page after someone books or joins a game, and under their booking. Only people with a
          confirmed booking can see it.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Court padlock code</div>
              <input style={inputStyle} value={access.padlockCode} onChange={function (e) { setAccessField("padlockCode", e.target.value); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Floodlights padlock code (blank = same)</div>
              <input style={inputStyle} value={access.floodlightCode} onChange={function (e) { setAccessField("floodlightCode", e.target.value); }} />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Who to contact on WhatsApp</div>
            <input style={inputStyle} value={access.contactText} onChange={function (e) { setAccessField("contactText", e.target.value); }} />
            <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 4 }}>
              {"Reads: \"…please reach out on WhatsApp to " + access.contactText + ".\""}
            </div>
          </div>
          <button style={btnStyle} onClick={saveAccess}>Save Access Codes</button>
          {accessSaved && <div style={{ fontSize: 12, color: colors.accent }}>Saved.</div>}
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Club Court Blocks</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Reserve courts for recurring weekly club sessions. Nobody can book those times, and they show on the Courts grid
          as club sessions. Skipping a date (e.g. a bank holiday) frees the courts for that day only.
        </p>
        {courtBlocks.map(function (b) {
          var upcomingSkips = (b.excludedDates || []).filter(function (d) { return d >= today; }).sort();
          return (
            <div key={b.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, opacity: b.active ? 1 : 0.55 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{b.label}{!b.active ? " · paused" : ""}</div>
                  <div style={{ fontSize: 12, color: colors.textDim }}>
                    {"Every " + DAY_NAMES[b.dayOfWeek] + " " + formatMin(b.startMin) + " – " + formatMin(b.endMin) + " · " +
                      (b.courts && b.courts.length ? "Court" + (b.courts.length > 1 ? "s " : " ") + b.courts.join(", ") : "All courts")}
                  </div>
                  {(b.fromDate || b.toDate) && (
                    <div style={{ fontSize: 11, color: colors.textFaint }}>{(b.fromDate ? "from " + b.fromDate : "") + (b.fromDate && b.toDate ? " " : "") + (b.toDate ? "until " + b.toDate : "")}</div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { updateDoc(doc(db, "courtBlocks", b.id), { active: !b.active }); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>{b.active ? "Pause" : "Resume"}</button>
                  <button onClick={function () { if (window.confirm("Delete this recurring session?")) deleteDoc(doc(db, "courtBlocks", b.id)); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }}>Delete</button>
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                {upcomingSkips.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                    {upcomingSkips.map(function (d) {
                      return (
                        <span key={d} style={{ fontSize: 12, background: colors.tealTint, color: colors.tealDark, padding: "3px 8px", borderRadius: 12, display: "flex", alignItems: "center", gap: 6 }}>
                          {d}
                          <button onClick={function () { updateDoc(doc(db, "courtBlocks", b.id), { excludedDates: arrayRemove(d) }); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.tealDark, fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} type="date" value={blockSkipInput[b.id] || ""} onChange={function (e) { var v = e.target.value; setBlockSkipInput(function (prev) { return { ...prev, [b.id]: v }; }); }} />
                  <button onClick={function () { addBlockSkip(b); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px", fontSize: 12 }}>Skip date</button>
                </div>
              </div>
            </div>
          );
        })}
        {courtBlocks.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No club sessions reserved yet.</p>}

        <form onSubmit={addCourtBlock} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Name</div>
              <input style={inputStyle} value={blockForm.label} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, label: v }; }); }} placeholder="Club night" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Day</div>
              <select style={inputStyle} value={blockForm.dayOfWeek} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, dayOfWeek: v }; }); }}>
                {DAY_NAMES.map(function (name, i) { return <option key={i} value={i}>{name}</option>; })}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>From</div>
              <input style={inputStyle} type="time" step="1800" value={blockForm.from} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, from: v }; }); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>To</div>
              <input style={inputStyle} type="time" step="1800" value={blockForm.to} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, to: v }; }); }} />
            </div>
          </div>
          <div>
            <div style={labelStyle}>Courts</div>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <input type="checkbox" checked={blockForm.allCourts} onChange={function (e) { var v = e.target.checked; setBlockForm(function (p) { return { ...p, allCourts: v }; }); }} />
              All courts
            </label>
            {!blockForm.allCourts && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Array.from({ length: (bookingDoc && bookingDoc.courtCount) || 1 }, function (_, i) { return i + 1; }).map(function (c) {
                  return (
                    <label key={c} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
                      <input type="checkbox" checked={blockForm.courts.indexOf(c) !== -1} onChange={function () { toggleBlockCourt(c); }} />
                      {"Court " + c}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Starts (optional)</div>
              <input style={inputStyle} type="date" value={blockForm.fromDate} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, fromDate: v }; }); }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Ends (optional)</div>
              <input style={inputStyle} type="date" value={blockForm.toDate} onChange={function (e) { var v = e.target.value; setBlockForm(function (p) { return { ...p, toDate: v }; }); }} />
            </div>
          </div>
          <button type="submit" style={btnStyle}>Reserve Weekly Session</button>
        </form>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Venues</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Shown on the public Sessions page, and court count sets the "session full" warning on the Poll.
        </p>
        {venues.map(function (v) {
          if (editingVenueId === v.id) {
            return (
              <div key={v.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, display: "flex", flexDirection: "column", gap: 8 }}>
                <input style={inputStyle} value={venueEdit.name} onChange={function (e) { setVenueEdit(function (p) { return { ...p, name: e.target.value }; }); }} placeholder="Venue name" />
                <input style={inputStyle} value={venueEdit.address} onChange={function (e) { setVenueEdit(function (p) { return { ...p, address: e.target.value }; }); }} placeholder="Address / postcode" />
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={venueEdit.courtCount} onChange={function (e) { setVenueEdit(function (p) { return { ...p, courtCount: e.target.value }; }); }} />
                  <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={venueEdit.indoor} onChange={function (e) { setVenueEdit(function (p) { return { ...p, indoor: e.target.checked }; }); }} />
                    Indoor
                  </label>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { saveVenue(v); }} style={{ ...btnStyle, width: "auto", padding: "8px 16px" }}>Save</button>
                  <button onClick={function () { setEditingVenueId(null); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 16px" }}>Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 14 }}>
              <div>
                <strong>{v.name}</strong>{v.address ? " — " + v.address : ""}
                <div style={{ fontSize: 12, color: colors.textDim }}>{(v.courtCount || 1) + " court(s)" + (v.indoor ? " · indoor" : "")}</div>
              </div>
              <button onClick={function () { startEditVenue(v); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>Edit</button>
            </div>
          );
        })}
        <form onSubmit={addVenue} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={labelStyle}>Venue Name</div>
            <input style={inputStyle} value={venueName} onChange={function (e) { setVenueName(e.target.value); }} placeholder="Wattisfield Village Hall" />
          </div>
          <div>
            <div style={labelStyle}>Address (optional)</div>
            <input style={inputStyle} value={venueAddress} onChange={function (e) { setVenueAddress(e.target.value); }} placeholder="..." />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Courts</div>
              <input style={inputStyle} type="number" min="1" value={venueCourts} onChange={function (e) { setVenueCourts(e.target.value); }} />
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "flex-end", paddingBottom: 11 }}>
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={venueIndoor} onChange={function (e) { setVenueIndoor(e.target.checked); }} />
                Indoor
              </label>
            </div>
          </div>
          <button type="submit" style={btnStyle}>Add Venue</button>
        </form>
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Weekly Sessions</h3>
        {templates.map(function (t) {
          var upcomingExclusions = (t.excludedDates || []).filter(function (d) { return d >= today; }).sort();
          if (editingTplId === t.id) {
            return (
              <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border, display: "flex", flexDirection: "column", gap: 8 }}>
                <select style={inputStyle} value={tplEdit.venueId} onChange={function (e) { setTplEdit(function (p) { return { ...p, venueId: e.target.value }; }); }}>
                  {venues.map(function (v) { return <option key={v.id} value={v.id}>{v.name}</option>; })}
                </select>
                <select style={inputStyle} value={tplEdit.dayOfWeek} onChange={function (e) { setTplEdit(function (p) { return { ...p, dayOfWeek: e.target.value }; }); }}>
                  {DAY_NAMES.map(function (name, i) { return <option key={i} value={i}>{name}</option>; })}
                </select>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input style={inputStyle} type="time" value={tplEdit.time} onChange={function (e) { setTplEdit(function (p) { return { ...p, time: e.target.value }; }); }} />
                  <span style={{ fontSize: 13, color: colors.textDim }}>to</span>
                  <input style={inputStyle} type="time" value={tplEdit.endTime} onChange={function (e) { setTplEdit(function (p) { return { ...p, endTime: e.target.value }; }); }} />
                </div>
                <input style={inputStyle} value={tplEdit.note} onChange={function (e) { setTplEdit(function (p) { return { ...p, note: e.target.value }; }); }} placeholder="Note shown on public Sessions page" />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { saveTemplate(t); }} style={{ ...btnStyle, width: "auto", padding: "8px 16px" }}>Save</button>
                  <button onClick={function () { setEditingTplId(null); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 16px" }}>Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid " + colors.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{formatDayTime(t.dayOfWeek, t.time, t.endTime)}</div>
                  <div style={{ fontSize: 12, color: colors.textDim }}>{t.venueName}{!t.active ? " · inactive" : ""}</div>
                  {t.note && <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 2 }}>{t.note}</div>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={function () { startEditTemplate(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>Edit</button>
                  <button onClick={function () { toggleActive(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12 }}>
                    {t.active ? "Pause" : "Resume"}
                  </button>
                  <button onClick={function () { removeTemplate(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }}>
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10, paddingLeft: 2 }}>
                <div style={{ fontSize: 11, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Skip a Date</div>
                {upcomingExclusions.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {upcomingExclusions.map(function (d) {
                      return (
                        <span key={d} style={{ fontSize: 12, background: colors.tealTint, color: colors.tealDark, padding: "3px 8px", borderRadius: 12, display: "flex", alignItems: "center", gap: 6 }}>
                          {d}
                          <button onClick={function () { removeExclusion(t, d); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.tealDark, fontWeight: 700, padding: 0, lineHeight: 1 }}>×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    style={{ ...inputStyle, width: "auto", flex: 1 }}
                    type="date"
                    value={excludeDateInput[t.id] || ""}
                    onChange={function (e) { setExcludeDateInput(function (prev) { return { ...prev, [t.id]: e.target.value }; }); }}
                  />
                  <button onClick={function () { addExclusion(t); }} style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px", fontSize: 12 }}>Exclude</button>
                </div>
              </div>
            </div>
          );
        })}
        {venues.length === 0 ? (
          <p style={{ fontSize: 13, color: colors.textDim }}>Add a venue first.</p>
        ) : (
          <form onSubmit={addTemplate} style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={labelStyle}>Venue</div>
              <select style={inputStyle} value={tplVenueId} onChange={function (e) { setTplVenueId(e.target.value); }}>
                <option value="">Select a venue…</option>
                {venues.map(function (v) { return <option key={v.id} value={v.id}>{v.name}</option>; })}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Day</div>
              <select style={inputStyle} value={tplDay} onChange={function (e) { setTplDay(e.target.value); }}>
                {DAY_NAMES.map(function (name, i) { return <option key={i} value={i}>{name}</option>; })}
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>From</div>
                <input style={inputStyle} type="time" value={tplTime} onChange={function (e) { setTplTime(e.target.value); }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={labelStyle}>To</div>
                <input style={inputStyle} type="time" value={tplEndTime} onChange={function (e) { setTplEndTime(e.target.value); }} />
              </div>
            </div>
            <div>
              <div style={labelStyle}>Note (shown on public Sessions page)</div>
              <input style={inputStyle} value={tplNote} onChange={function (e) { setTplNote(e.target.value); }} placeholder="For all standards — beginners welcome" />
            </div>
            <button type="submit" style={btnStyle}>Add Weekly Session</button>
          </form>
        )}
      </div>
    </div>
  );
}
